import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import AXIOS from "../../../config/api";
import { BASE_API } from "../../../constants/apiUrls";
import { toast } from "../../../lib/toast";
import {
  ErrorMessage,
  GetCommonData,
  GetSingleDataParams,
  Params,
} from "../../../types";
import {
  AttendanceData,
  AttendanceDataParams,
  SyncLogParams,
} from "../../../types/attendance/attendance";

// get all
export const getAllAttendanceAsync = createAsyncThunk<
  GetCommonData<AttendanceData>,
  Params<AttendanceDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>("admin/getAllAttendance", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.attendance, {
      params,
    });

    return {
      data: data.data?.getData,
      length: data.data?.totalCount,
    };
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get attendance fail");
    return rejectWithValue(error);
  }
});

// get sync logs
export const getSyncLogsAsync = createAsyncThunk<
  GetCommonData<any>,
  Params<SyncLogParams>,
  {
    rejectValue: ErrorMessage;
  }
>("admin/getSyncLogs", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(BASE_API.syncLogs, {
      params,
    });

    return {
      data: data.data?.data, // Assuming backend returns array in `data` and total in `totalCount` or `total`
      length: data.data?.totalCount, // Double check this structure if backend changes
    };
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Get sync logs fail");
    return rejectWithValue(error);
  }
});

// single
export const singleAttendanceAsync = createAsyncThunk<
  any,
  Params<GetSingleDataParams>
>("admin/singleAttendance", async ({ params }, { rejectWithValue }) => {
  try {
    const data = await AXIOS.get(`${BASE_API.attendance}/${params?.id}`, {
      params,
    });

    return data.data?.areas as any;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Attendance data fail");
    return rejectWithValue(error);
  }
});

export const exportAttendanceAsync = createAsyncThunk<
  Blob,
  Params<AttendanceDataParams>,
  {
    rejectValue: ErrorMessage;
  }
>("admin/exportAttendance", async ({ params }, { rejectWithValue }) => {
  try {
    const response = await AXIOS.get(BASE_API.exportAttendance, {
      params,
      responseType: "blob",
    });

    return response.data;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Export attendance failed");
    return rejectWithValue(error);
  }
});

export const syncOfflineAttendanceAsync = createAsyncThunk<
  null,
  { option: { reRender: any } },
  {
    rejectValue: ErrorMessage;
  }
>("admin/syncOfflineAttendance", async (_, { rejectWithValue }) => {
  try {
    const data = await AXIOS.patch(BASE_API.offlineAttendance);
    toast(
      "success",
      data?.data?.message || "Attendance checked and synced successfully"
    );
    return null;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast("error", error?.message || "Attendance checked and synced fail");
    return rejectWithValue(error);
  }
});

// create
export const exportCheckInToErp = createAsyncThunk<
  null,
  { attendanceId: any; reRender: (value: boolean) => void } // Define the type for the payload
>(
  "admin/exportCheckInToErp",
  async ({ attendanceId, reRender }, { rejectWithValue }) => {
    try {
      // Post the request with the dynamic attendanceId in the URL
      await AXIOS.post(`${BASE_API.exportCheckInToErp}/${attendanceId}`);

      // Call reRender if needed
      reRender(true);
      toast("success", "Attendance import to erp system successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Attendance import to erp system fail");
      return rejectWithValue(error);
    }
  }
);

export const exportCheckOutToErp = createAsyncThunk<
  null,
  { attendanceId: any; reRender: (value: boolean) => void } // Define the type for the payload
>(
  "admin/exportCheckOutToErp",
  async ({ attendanceId, reRender }, { rejectWithValue }) => {
    try {
      // Post the request with the dynamic attendanceId in the URL
      await AXIOS.post(`${BASE_API.exportCheckOutToErp}/${attendanceId}`);

      // Call reRender if needed
      reRender(true);
      toast("success", "Attendance import to erp system successfully");
      return null;
    } catch (err) {
      const errors = err as AxiosError;
      const error = errors.response?.data as ErrorMessage;
      toast("error", error?.message || "Attendance import to erp system fail");
      return rejectWithValue(error);
    }
  }
);

type DataT = {
  attendanceIds: string[];
  type: "in" | "out";
};

export const exportBulkToErp = createAsyncThunk<
  null,
  { body: DataT; reRender: (value: boolean) => void } // Define the type for the payload
>("admin/exportBulkToErp", async ({ body, reRender }, { rejectWithValue }) => {
  try {
    // Post the request with the dynamic attendanceId in the URL
    await AXIOS.post(`${BASE_API.exportBulkToErp}`, body);

    // Call reRender if needed
    reRender(true);
    toast("success", "Attendance bulk import to erp system successfully");
    return null;
  } catch (err) {
    const errors = err as AxiosError;
    const error = errors.response?.data as ErrorMessage;
    toast(
      "error",
      error?.message || "Attendance bulk import to erp system fail"
    );
    return rejectWithValue(error);
  }
});
